import { useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useMeters, useVoltageQuality } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
  StatCard,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function VoltageQualityPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);

  const { data, isLoading } = useVoltageQuality(meterId || meters[0], days);
  const points = data?.data ?? [];
  const thresholds = data?.thresholds ?? {
    upper: 241.5,
    lower: 218.5,
    nominal: 230,
  };

  const overCount = points.filter((p) => p.status === "over_voltage").length;
  const underCount = points.filter((p) => p.status === "under_voltage").length;
  const normalCount = points.filter((p) => p.status === "normal").length;

  const chartData = points.map((p) => ({
    ts: fmt.dt(p.timestamp),
    voltage: p.voltage,
    color:
      p.status === "normal"
        ? CHART_COLORS.blue
        : p.status === "over_voltage"
          ? CHART_COLORS.red
          : CHART_COLORS.amber,
  }));

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Voltage Quality"
          subtitle="Supply voltage with ±5% threshold bands"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <DaysSelect value={days} onChange={setDays} />
        </PageHeader>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Normal Readings"
            value={normalCount}
            color="emerald"
          />
          <StatCard label="Over Voltage" value={overCount} color="red" />
          <StatCard label="Under Voltage" value={underCount} color="amber" />
        </div>

        <ChartCard
          title="Voltage Over Time"
          subtitle={`Thresholds: ${thresholds.lower}V – ${thresholds.upper}V (nominal ${thresholds.nominal}V)`}
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="ts"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis
                  domain={[210, 250]}
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="ra-tooltip">
                        <p className="text-slate-400 mb-1">{label}</p>
                        <p className="font-mono text-blue-600">
                          Voltage: {payload[0]?.value?.toFixed(2)} V
                        </p>
                      </div>
                    );
                  }}
                />
                <ReferenceLine
                  y={thresholds.upper}
                  stroke={CHART_COLORS.red}
                  strokeDasharray="4 2"
                  label={{
                    value: `+5% (${thresholds.upper}V)`,
                    fill: CHART_COLORS.red,
                    fontSize: 10,
                  }}
                />
                <ReferenceLine
                  y={thresholds.nominal}
                  stroke={CHART_COLORS.slate}
                  strokeDasharray="4 2"
                  label={{
                    value: `Nominal (${thresholds.nominal}V)`,
                    fill: "#94A3B8",
                    fontSize: 10,
                  }}
                />
                <ReferenceLine
                  y={thresholds.lower}
                  stroke={CHART_COLORS.amber}
                  strokeDasharray="4 2"
                  label={{
                    value: `-5% (${thresholds.lower}V)`,
                    fill: CHART_COLORS.amber,
                    fontSize: 10,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="voltage"
                  stroke={CHART_COLORS.blue}
                  dot={false}
                  strokeWidth={1.5}
                  name="Voltage"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </Layout>
  );
}
